/**
 * What the trainer has in hand in the discussion, as opposed to what the thread holds: the draft in
 * the composer, the comments that failed to send, the comment being edited, and the last error.
 *
 * All of it belongs to ONE clip. The player switches clips without closing, so a draft, an unsent
 * comment or an edit written on one clip must never be sent to, or shown on, the next. A draft is
 * parked when its clip is left and comes back when the trainer returns; an unsent comment keeps the
 * clip it was written on and is offered only there; an edit and an error end with their clip.
 */

export type DiscussionError = 'sendFailed' | 'deleteFailed';

export interface UnsentComment {
  readonly key: number;
  readonly clipId: number;
  readonly body: string;
  readonly offsetMs: number | null;
}

export interface CommentEdit {
  readonly id: number;
  readonly body: string;
  /** The rewrite is on its way; the field stays open until the server has it. */
  readonly saving: boolean;
  readonly failed: boolean;
}

export interface DiscussionState {
  readonly clipId: number;
  readonly draft: string;
  /** Drafts left on other clips, by clip. */
  readonly parked: ReadonlyMap<number, string>;
  readonly unsent: readonly UnsentComment[];
  readonly editing: CommentEdit | null;
  readonly error: DiscussionError | null;
  readonly nextKey: number;
}

export type DiscussionEvent =
  | { readonly kind: 'show'; readonly clipId: number }
  | { readonly kind: 'type'; readonly text: string }
  | { readonly kind: 'send' }
  | {
      readonly kind: 'sendFailed';
      readonly clipId: number;
      readonly body: string;
      readonly offsetMs: number | null;
    }
  | { readonly kind: 'resend'; readonly key: number }
  | { readonly kind: 'discard'; readonly key: number }
  | { readonly kind: 'editStart'; readonly id: number; readonly body: string }
  | { readonly kind: 'editType'; readonly body: string }
  | { readonly kind: 'editCancel' }
  | { readonly kind: 'editSave' }
  | { readonly kind: 'editSettled'; readonly clipId: number; readonly id: number; readonly ok: boolean }
  | { readonly kind: 'deleteFailed'; readonly clipId: number };

export function initialDiscussion(clipId: number): DiscussionState {
  return { clipId, draft: '', parked: new Map(), unsent: [], editing: null, error: null, nextKey: 1 };
}

/** The comments that failed to send on the clip on screen, and only those. */
export function unsentHere(state: DiscussionState): readonly UnsentComment[] {
  return state.unsent.filter((entry) => entry.clipId === state.clipId);
}

export function discussionReducer(state: DiscussionState, event: DiscussionEvent): DiscussionState {
  switch (event.kind) {
    case 'show': {
      if (event.clipId === state.clipId) return state;
      const parked = new Map(state.parked);
      if (state.draft.trim() === '') parked.delete(state.clipId);
      else parked.set(state.clipId, state.draft);
      const draft = parked.get(event.clipId) ?? '';
      parked.delete(event.clipId);
      return { ...state, clipId: event.clipId, draft, parked, editing: null, error: null };
    }
    case 'type':
      return { ...state, draft: event.text };
    case 'send':
      return { ...state, draft: '', error: null };
    case 'sendFailed': {
      // Back into the field only when it is this clip's field and the trainer has not typed on.
      if (event.clipId === state.clipId && state.draft.trim() === '') {
        return { ...state, draft: event.body, error: 'sendFailed' };
      }
      const entry: UnsentComment = {
        key: state.nextKey,
        clipId: event.clipId,
        body: event.body,
        offsetMs: event.offsetMs,
      };
      return { ...state, unsent: [...state.unsent, entry], nextKey: state.nextKey + 1 };
    }
    case 'resend':
    case 'discard':
      return { ...state, unsent: state.unsent.filter((entry) => entry.key !== event.key) };
    case 'editStart':
      return { ...state, editing: { id: event.id, body: event.body, saving: false, failed: false } };
    case 'editType':
      return state.editing === null ? state : { ...state, editing: { ...state.editing, body: event.body } };
    case 'editCancel':
      return state.editing?.saving === true ? state : { ...state, editing: null };
    case 'editSave':
      return state.editing === null || state.editing.saving
        ? state
        : { ...state, editing: { ...state.editing, saving: true, failed: false } };
    case 'editSettled': {
      const editing = state.editing;
      if (event.clipId !== state.clipId || editing?.id !== event.id) return state;
      return event.ok
        ? { ...state, editing: null }
        : { ...state, editing: { ...editing, saving: false, failed: true } };
    }
    case 'deleteFailed':
      return event.clipId === state.clipId ? { ...state, error: 'deleteFailed' } : state;
  }
}
